// Minimal hyperscript helper — no framework. Creates DOM elements.
type Attrs = Record<string, string | number | boolean | EventListener | undefined>;
type Child = Node | string | null | undefined | Child[];

export function h<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Attrs = {},
  ...children: Child[]
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (v === undefined || v === false) continue;
    if (k.startsWith("on") && typeof v === "function") {
      el.addEventListener(k.slice(2).toLowerCase(), v as EventListener);
    } else if (k === "class") {
      el.className = String(v);
    } else if (k === "html") {
      el.innerHTML = String(v);
    } else {
      el.setAttribute(k, String(v));
    }
  }
  append(el, children);
  return el;
}

function append(el: HTMLElement, children: Child[]): void {
  for (const c of children) {
    if (c === null || c === undefined) continue;
    if (Array.isArray(c)) append(el, c);
    else if (typeof c === "string") el.appendChild(document.createTextNode(c));
    else el.appendChild(c);
  }
}

export function mount(root: HTMLElement, node: Node): void {
  root.replaceChildren(node);
}

/**
 * Make a non-button element behave like a button for keyboard users:
 * activates on click, Enter, and Space. Pair with role="button" tabindex="0".
 */
export function onActivate(el: HTMLElement, fn: () => void): void {
  el.addEventListener("click", fn);
  el.addEventListener("keydown", (e) => {
    const key = (e as KeyboardEvent).key;
    if (key === "Enter" || key === " " || key === "Spacebar") {
      e.preventDefault();
      fn();
    }
  });
}
